/**
 * PDP v2 set row element (019 §8, S2): <pdp-set-row>; <pdp-set-price> is in
 * pdp-v2-set-price.js.
 * Loaded after pdp-v2.js and pdp-v2-set-api.js (the quote maths and the
 * set-add owner, theme.pdpV2.setQuote / addSet); split for the §11 budget
 * (plan 001 A6). Renders from Collection Offer state on every trigger.
 * Defines nothing without the API file, so the row keeps its fallback link.
 *
 * Plan 001: with the block opening on colours (config.multi) the colours tab
 * is a multi-add (A2-A4): tiles tick on and off, each colour in the size
 * chosen on the main product, and "Add N to bag" adds the main product and
 * every ticked colour in one POST. The plain colours row (A5, config.plain)
 * is the same multi-add with no offer and never reads collection-offer.js.
 * An offer row whose slot is no longer live hides until reload, with its
 * sticky link (002-F1). A7 (Mat, 2026-10-23): while colours are ticked the
 * main and sticky Add to bag add them too (pdp-v2-set-buttons.js).
 */
(function () {
  'use strict';

  var theme = window.theme = window.theme || {};
  var api = theme.pdpV2;
  if (!api || typeof api.addSet !== 'function' || api.__setInitialised) return;
  api.__setInitialised = true;

  var coApi = api.coApi;
  var fill = api.fill;
  var mutation = api.mutation;

  var byId = function (pieces, id) {
    return pieces.find(function (p) { return String(p.id) === String(id); }) || null;
  };

  /** "M" for "Medium": a piece variant's short label for the size, else the value itself. */
  function shortSize(pieces, size) {
    for (var i = 0; i < pieces.length; i++) {
      var v = (pieces[i].variants || []).find(function (x) { return String(x.option) === String(size); });
      if (v && v.short) return v.short;
    }
    return String(size);
  }

  /** A line for addSet and its tracking: one piece in one variant. */
  function pieceItem(p, v) {
    return { variantId: v.id, price: v.price, compareAt: v.compareAt, title: p.title, productId: p.id, url: p.url, image: p.image || '', vendor: p.vendor, type: p.type };
  }

  // ---- <pdp-set-row> ----
  class PdpSetRow extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.injected = api.isInjected(this);
      if (this.injected) { this.classList.add('is-injected'); return; }
      this.config = api.readIsland(this, 'script[data-set-config]') || {};
      this.plain = !!this.config.plain;
      this.form = document.querySelector('.section-main-product > product-form.pdp-v2');
      // A restored snapshot (prefetch cache, back/forward) carries this marker
      // from its previous life; a fresh server render does not (§8.6).
      var restored = this.hasAttribute('data-hydrated');
      this.setAttribute('data-hydrated', '');
      this.state = { tab: this.config.firstTab || 'tops', pieceId: null, pieceVariantId: null, picks: [] };
      var alertEl = this.querySelector('[data-alert]');
      if (alertEl) alertEl.textContent = '';
      var live = this.querySelector('[data-live]');
      if (live) live.textContent = '';

      this._onClick = this.handleClick.bind(this);
      this._onRender = this.render.bind(this);
      this.addEventListener('click', this._onClick);
      if (api.setButtons) api.setButtons.attach(this);
      document.addEventListener('on:collection-offer:state', this._onRender);
      if (this.form) this.form.addEventListener('on:variant:change', this._onRender);

      // §8.6/§8.8: a snapshot's serialised busy state never survives; derive it from the owner.
      this.setBusy(!!mutation.pending);
      // The offer row needs Collection Offer state; the plain row never does (002-F2).
      if (!this.plain && !coApi()) { this.expire(); return; }
      var self = this;
      if (mutation.pending) mutation.pending.then(function (o) { self.setBusy(false); self.finish(o); });
      this.hidden = false; // the server never renders it hidden; a restored snapshot may carry an earlier life's
      this.render();
      this.injectStickyLink();
      if (restored) {
        var co = coApi();
        if (document.querySelector('cart-drawer')) document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        else if (co && typeof co.refresh === 'function') co.refresh();
      }
    }

    disconnectedCallback() {
      if (!this._connected) return;
      this._connected = false;
      this.removeEventListener('click', this._onClick);
      document.removeEventListener('on:collection-offer:state', this._onRender);
      if (this.form) this.form.removeEventListener('on:variant:change', this._onRender);
      if (api.setButtons) api.setButtons.release(this);
      if (this.stickyLink && this.stickyLink.parentNode) this.stickyLink.parentNode.removeChild(this.stickyLink);
      this.stickyLink = null;
    }

    offer() {
      return { slot: Number(this.config.slot), percentage: Number(this.config.percentage), min: Number(this.config.min) };
    }

    /** A3: the colours tab is a multi-add when the block opens on colours, and always on the plain row. */
    multi() {
      return !!this.config.multi && this.state.tab === 'colours';
    }

    /** 002-F1: the slot ended (or this page never knew it): hidden until reload with the sticky link; never the plain row. */
    expire() {
      this.expired = true;
      this.hidden = true;
      var section = this.closest('.section-main-product');
      var link = this.stickyLink || (section && section.querySelector('sticky-atc .pdp-v2-sticky-set'));
      if (link && link.parentNode) link.parentNode.removeChild(link);
      this.stickyLink = null;
      if (api.setButtons) api.setButtons.sync(this);
    }

    pantUnit() {
      var picker = this.form && this.form.querySelector('variant-picker');
      var data = picker && typeof picker.getVariantData === 'function' ? picker.getVariantData() : null;
      var variants = (data && data.variants) || [];
      var selected = picker && picker.variant ? picker.variant : null;
      var v = selected || variants.find(function (x) { return x.available; }) || variants[0];
      if (!v) return null;
      // A2: the chosen size, as the value of the main product's Size option.
      var size = selected ? (selected.options ? selected.options[Number(this.config.sizeIndex) || 0] : selected.option1) : null;
      // available: false when the chosen size is sold out on the main product (main.js keeps it selected).
      return { id: v.id, price: v.price, compareAt: v.compare_at_price || 0, chosen: !!selected, available: !!v.available, size: size === undefined ? null : size };
    }

    /** The main product is sold out in the size chosen above: "This colour is sold out in M. ..." (pairing: "M sold out"). */
    mainOut(size) {
      var copy = this.config.copy || {};
      var s = shortSize(this.config.pieces || [], size);
      return copy.mainSoldOut ? fill(copy.mainSoldOut, { size: s }) : s + ' ' + copy.soldOut;
    }

    mainItem(pant) {
      var titleEl = document.querySelector('.section-main-product .pdp-v2 h1.product-title');
      // E4: the main line is untagged, so it merges with the same variant already in the bag.
      return { variantId: pant.id, untagged: true, price: pant.price, compareAt: pant.compareAt, title: this.config.title || (titleEl ? titleEl.textContent.trim() : ''), productId: this.config.productId, url: location.pathname, image: this.config.image || '', vendor: this.config.vendor || '', type: this.config.type || '' };
    }

    piece() {
      return byId(this.config.pieces || [], this.state.pieceId);
    }

    picks() {
      var pieces = this.config.pieces || [];
      return this.state.picks.map(function (id) { return byId(pieces, id); }).filter(Boolean);
    }

    money(cents) {
      return typeof theme.formatMoney === 'function' ? theme.formatMoney(cents, { currency: this.config.currency }) : String(cents / 100);
    }

    handleClick(evt) {
      var t = evt.target;
      if (!t || !t.closest) return;
      var tab = t.closest('[data-tab]');
      var tile = t.closest('.pdp-v2-set__tile');
      var chip = t.closest('[data-piece-variant]');
      var cta = t.closest('[data-cta]');
      var dismiss = t.closest('[data-dismiss]');
      if (dismiss) {
        if (mutation.lastOutcome) mutation.lastOutcome.dismissed = true;
        this.querySelector('[data-alert]').textContent = '';
        return;
      }
      if (tab) {
        this.state.tab = tab.getAttribute('data-tab');
        this.state.pieceId = null;
        this.state.pieceVariantId = null;
        this.state.picks = [];
        this.render();
        // §8.11: the tile list updates in place; announce its count politely.
        var shown = this.querySelectorAll('[data-tiles="' + this.state.tab + '"] .pdp-v2-set__tile').length;
        var liveTab = this.querySelector('[data-live]');
        if (liveTab) liveTab.textContent = fill((this.config.copy || {}).tabShown || '{count} shown: {tab}', { count: shown, tab: tab.textContent.trim() });
      } else if (tile) {
        var id = tile.getAttribute('data-piece');
        if (this.multi()) { this.toggle(tile, id); return; }
        var same = String(this.state.pieceId) === String(id);
        this.state.pieceId = same ? null : id;
        this.state.pieceVariantId = null;
        if (!same) api.track('set_item_select', { piece_product_id: String(id), tab: this.state.tab });
        this.render();
        var keep = this.querySelector('.pdp-v2-set__tile[data-piece="' + id + '"]');
        if (keep) keep.focus();
      } else if (chip && !chip.disabled) {
        this.state.pieceVariantId = chip.getAttribute('data-piece-variant');
        this.querySelector('[data-alert]').textContent = '';
        this.render();
        var keepChip = this.querySelector('[data-piece-variant="' + this.state.pieceVariantId + '"]');
        if (keepChip) keepChip.focus();
      } else if (cta) {
        this.submit(cta);
      }
    }

    /** A3: a colour ticks on and off; one sold out in the chosen size cannot be ticked. */
    toggle(tile, id) {
      if (tile.getAttribute('aria-disabled') === 'true') return;
      var picks = this.state.picks;
      var at = picks.indexOf(String(id));
      if (at >= 0) {
        picks.splice(at, 1);
      } else {
        picks.push(String(id));
        api.track('set_item_select', { piece_product_id: String(id), tab: this.state.tab });
      }
      this.render();
      var keep = this.querySelector('.pdp-v2-set__tile[data-piece="' + id + '"]');
      if (keep) keep.focus();
    }

    /** cta: the button pressed; A7 passes the main or sticky one with its pdp_add source. */
    submit(cta, source) {
      if (mutation.pending) return;
      var alertEl = this.querySelector('[data-alert]');
      alertEl.textContent = '';
      if (mutation.lastOutcome) mutation.lastOutcome.dismissed = true;
      var copy = this.config.copy || {};
      var pant = this.pantUnit();
      var multi = this.multi();
      var picks = multi ? this.picks() : [];
      var piece = multi ? null : this.piece();
      if (multi ? !picks.length : !piece) return;
      if (!pant || !pant.chosen) {
        alertEl.textContent = multi && copy.chooseSize || copy.needPantSize;
        if (multi && api.setButtons) api.setButtons.toSize(this);
        else if (this.form && typeof this.form.validate === 'function') this.form.validate();
        return;
      }
      // Every line goes in one POST, so a sold-out main line would fail the whole add.
      if (!pant.available) { alertEl.textContent = this.mainOut(pant.size); return; }
      var items = [this.mainItem(pant)];
      if (multi) {
        // A2: each ticked colour in the main product's size; one sold out meanwhile is left out.
        picks.forEach(function (p) {
          var m = api.sizeVariant(p, pant.size);
          if (m.ok) items.push(pieceItem(p, m.variant));
        });
        if (items.length < 2) return;
      } else {
        var chosenId = String(this.state.pieceVariantId);
        var pv = piece.variants.find(function (v) { return String(v.id) === chosenId; });
        if (!pv) {
          alertEl.textContent = fill(copy.needPieceSize, { piece: piece.shortTitle });
          var group = this.querySelector('[data-piece-sizes]');
          if (group) group.setAttribute('aria-describedby', alertEl.id || '');
          var first = this.querySelector('[data-piece-variant]:not([disabled])');
          if (first) first.focus();
          return;
        }
        items.push(pieceItem(piece, pv));
      }
      var self = this;
      this.setBusy(true);
      return api.addSet({
        items: items,
        currency: this.config.currency,
        copy: copy,
        opener: cta,
        surface: this.plain ? 'colours' : (this.state.tab === 'colours' ? 'set_colours' : 'set_pair'),
        slot: this.plain ? 0 : Number(this.config.slot),
        source: source || (this.plain ? 'pdp_colours' : 'pdp_set'),
        plain: this.plain
      }).then(function (outcome) { self.setBusy(false); self.finish(outcome); return outcome; });
    }

    finish(outcome) {
      if (!outcome || !this._connected) return;
      var copy = this.config.copy || {};
      if (outcome.kind === 'confirmed') {
        this.state.pieceId = null;
        this.state.pieceVariantId = null;
        this.state.picks = [];
        var live = this.querySelector('[data-live]');
        if (live) live.textContent = copy.added;
      }
      this.render();
    }

    setBusy(on) {
      var cta = this.querySelector('[data-cta]');
      if (!cta) return;
      cta.disabled = !!on;
      if (on) cta.setAttribute('aria-busy', 'true'); else cta.removeAttribute('aria-busy');
    }

    /** Units [{price, compareAt}]: the main product first, then each piece. */
    quoteFor(co, offer, cartLines, units) {
      // §8.2: before the first state publication the bag is unknown, not empty:
      // full prices only, no qualification claims.
      if (!this.known) {
        var rows = units.map(function (u) { return { net: u.price, full: Math.max(u.price, u.compareAt || 0) }; });
        return { unknown: true, qualified: false, rows: rows, total: rows.reduce(function (n, r) { return n + r.net; }, 0), was: null, save: 0, bagDrops: false, bagSaving: 0 };
      }
      return api.setQuote(co, offer, cartLines, units.map(function (u) { return { price: u.price, compareAt: u.compareAt }; }));
    }

    /** A4: "Save $17.60 on 2", plus the free-shipping clause when the bag after the add newly clears it. */
    saveLine(quote, count) {
      var copy = this.config.copy || {};
      if (!this.config.multi) return copy.save + ' ' + this.money(quote.save);
      var ship = api.crossesFreeShipping(api.shippingBar(), quote) && copy.freeShipping ? ' ' + copy.freeShipping : '';
      return fill(copy.saveOn, { saving: this.money(quote.save), count: count }) + ship;
    }

    /** Every trigger draws the row, then the Add to bag buttons follow it (A7). */
    render() {
      this.draw();
      if (api.setButtons) api.setButtons.sync(this);
    }

    draw() {
      var co = coApi();
      if (!this.config) return;
      var offer = this.offer();
      // 002-F1: an offer row checks its slot is still live before any offer maths.
      if (!this.plain && (this.expired || !api.liveOffer(co, offer))) { this.expire(); return; }
      var copy = this.config.copy || {};
      var st = co && typeof co.getState === 'function' ? co.getState() : { loaded: false };
      this.known = this.plain || !!(st && st.loaded && st.cart && Array.isArray(st.cart.lines));
      var cartLines = !this.plain && this.known ? st.cart.lines : [];
      var pant = this.pantUnit();
      var pieces = this.config.pieces || [];
      var size = pant && pant.chosen ? pant.size : null;
      var self = this;

      // A2: a size change re-evaluates every colour; a ticked one sold out in the new size drops out.
      if (size !== null) {
        this.state.picks = this.state.picks.filter(function (id) { var p = byId(pieces, id); return p && api.sizeVariant(p, size).ok; });
      }

      this.querySelectorAll('[data-tab]').forEach(function (b) {
        b.setAttribute('aria-pressed', String(b.getAttribute('data-tab') === self.state.tab));
      });
      this.querySelectorAll('[data-tiles]').forEach(function (list) {
        list.hidden = list.getAttribute('data-tiles') !== self.state.tab;
      });
      this.querySelectorAll('.pdp-v2-set__tile').forEach(function (tile) {
        var p = byId(pieces, tile.getAttribute('data-piece'));
        if (!p) return;
        if (self.config.multi && p.tab === 'colours') { self.renderColour(tile, p, co, offer, cartLines, pant, size); return; }
        tile.setAttribute('aria-pressed', String(String(self.state.pieceId) === String(p.id)));
        var v = p.variants.find(function (x) { return x.available; }) || p.variants[0];
        if (!pant || !v) return;
        var q = self.quoteFor(co, offer, cartLines, [pant, v]);
        var price = q.qualified ? self.money(q.rows[1].net) + ' ' + copy.inASet : self.money(v.price);
        tile.setAttribute('aria-label', p.shortTitle + ', ' + (p.colour || '') + ', ' + price);
      });

      var multi = this.multi();
      var picks = multi ? this.picks() : [];
      var piece = multi ? null : this.piece();
      var open = multi ? picks.length > 0 : !!piece;
      var summary = this.querySelector('[data-summary]');
      var picked = this.querySelector('[data-picked]');
      if (summary) summary.hidden = open;
      if (picked) picked.hidden = !open;
      // The multi-add cannot add a main product sold out in the size chosen above (renderPicks says why).
      var ctaEl = this.querySelector('[data-cta]');
      if (ctaEl && !ctaEl.hasAttribute('aria-busy')) ctaEl.disabled = multi && !!pant && pant.chosen && !pant.available;

      var foldedSub = this.querySelector('[data-folded-sub]');
      if (foldedSub && pant) {
        var tabPieces = pieces.filter(function (x) { return x.tab === self.state.tab; });
        var probePiece = tabPieces[0];
        var probe = probePiece ? (probePiece.variants.find(function (x) { return x.available; }) || probePiece.variants[0]) : null;
        var q0 = probe ? this.quoteFor(co, offer, cartLines, [pant, probe]) : null;
        if (q0 && !q0.qualified && !q0.unknown && !this.plain) {
          foldedSub.textContent = fill(copy.notQualified, { remaining: q0.remaining, collection: this.config.collection, percentage: offer.percentage, min: offer.min });
        } else {
          var base = this.state.tab === 'tops' ? copy.foldedTops : copy.foldedColours;
          if (this.state.tab === 'tops' && q0 && q0.qualified) {
            var nets = tabPieces.map(function (x) {
              var v2 = x.variants.find(function (y) { return y.available; });
              return v2 ? self.quoteFor(co, offer, cartLines, [pant, v2]).rows[1].net : Infinity;
            });
            var from = Math.min.apply(null, nets);
            if (isFinite(from)) base = base + ' ' + copy.fromPrefix + ' ' + this.money(from) + '.';
          } else if (multi && q0 && q0.qualified && q0.save > 0) {
            base = base + ' ' + fill(copy.saveOn, { saving: this.money(q0.save), count: 2 }) + '.';
          }
          foldedSub.textContent = base;
        }
      }

      if (!open || !pant) { this.renderNote(co, offer, cartLines, pant, null); return; }
      if (multi) { this.renderPicks(co, offer, cartLines, pant, picks, size); return; }

      var chosenId = String(this.state.pieceVariantId);
      var pv = piece.variants.find(function (x) { return String(x.id) === chosenId; }) || null;
      var unit = pv || piece.variants.find(function (x) { return x.available; }) || piece.variants[0];
      var quote = this.quoteFor(co, offer, cartLines, [pant, unit]);

      var info = this.querySelector('[data-piece-info]');
      info.textContent = '';
      var name = document.createElement('span'); name.className = 'pdp-v2-set__name'; name.textContent = piece.shortTitle; info.appendChild(name);
      var meta = document.createElement('span'); meta.className = 'pdp-v2-set__meta'; meta.textContent = piece.colour || ''; info.appendChild(meta);
      var pr = document.createElement('span'); pr.className = 'pdp-v2-set__price';
      if (quote.qualified) {
        var now = document.createElement('span'); now.className = 'pdp-v2-set__now'; now.textContent = this.money(quote.rows[1].net) + ' ' + copy.inASet;
        var rrp = document.createElement('s'); rrp.textContent = this.money(quote.rows[1].full);
        pr.appendChild(now); pr.appendChild(document.createTextNode(' ')); pr.appendChild(rrp);
      } else {
        pr.textContent = this.money(unit.price);
      }
      info.appendChild(pr);

      var sizes = this.querySelector('[data-piece-sizes]');
      // §8.11: a rebuild (e.g. on a state event) keeps keyboard focus on the same chip,
      // else on the first available one.
      var act = document.activeElement;
      var hadFocus = !!act && sizes.contains(act) && act.getAttribute('data-piece-variant');
      sizes.textContent = '';
      var labelId = 'pdp-v2-set-size-' + piece.id;
      var lab = document.createElement('span'); lab.id = labelId; lab.className = 'pdp-v2-set__size-label';
      lab.textContent = fill(copy.pieceSizeLabel, { piece: piece.shortTitle }) + ' — ' + (pv ? pv.short || pv.title : copy.selectPlaceholder);
      sizes.appendChild(lab);
      sizes.setAttribute('role', 'group');
      sizes.setAttribute('aria-labelledby', labelId);
      var grid = document.createElement('div'); grid.className = 'pdp-v2-set__chips';
      piece.variants.forEach(function (v) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'pdp-v2-set__chip';
        b.setAttribute('data-piece-variant', v.id);
        b.setAttribute('aria-pressed', String(!!pv && String(pv.id) === String(v.id)));
        var nm = v.short || v.option || v.title;
        b.innerHTML = '<span class="js-value"></span>' + (v.au ? '<span class="opt-label__au">AU ' + v.au + '</span>' : '');
        b.firstChild.textContent = nm;
        b.setAttribute('aria-label', nm + (v.au ? ', AU ' + v.au : '') + (v.available ? '' : ', ' + copy.soldOut));
        if (!v.available) b.disabled = true;
        grid.appendChild(b);
      });
      sizes.appendChild(grid);
      if (hadFocus) {
        var back = grid.querySelector('[data-piece-variant="' + hadFocus + '"]:not([disabled])') || grid.querySelector('[data-piece-variant]:not([disabled])');
        if (back) back.focus();
      }

      var totals = this.querySelector('[data-totals]');
      totals.textContent = '';
      if (quote.qualified) {
        var was = document.createElement('s'); was.textContent = this.money(quote.was);
        var save = document.createElement('span'); save.className = 'pdp-v2-set__save'; save.textContent = this.saveLine(quote, 2);
        totals.appendChild(was);
        totals.appendChild(document.createTextNode(' ' + this.money(quote.total) + ' '));
        totals.appendChild(save);
      } else {
        totals.textContent = this.money(quote.total);
      }
      var cta = this.querySelector('[data-cta]');
      if (cta && !cta.hasAttribute('aria-busy')) cta.textContent = copy.cta + ' — ' + this.money(quote.total);
      this.renderNote(co, offer, cartLines, pant, quote);
    }

    /** A2/A3: a colour tile in the size chosen above: ticked, or "Sold out in M" and not tickable. */
    renderColour(tile, p, co, offer, cartLines, pant, size) {
      var copy = this.config.copy || {};
      var m = api.sizeVariant(p, size);
      var out = size !== null && !m.ok;
      var soldText = out ? fill(copy.soldOutIn, { size: shortSize(this.config.pieces || [], size) }) : '';
      tile.setAttribute('aria-pressed', String(this.state.picks.indexOf(String(p.id)) >= 0));
      if (out) tile.setAttribute('aria-disabled', 'true'); else tile.removeAttribute('aria-disabled');
      var tag = tile.querySelector('.pdp-v2-set__soldout');
      if (out && !tag) {
        tag = document.createElement('span');
        tag.className = 'pdp-v2-set__soldout';
        tag.setAttribute('aria-hidden', 'true');
        tile.appendChild(tag);
      }
      if (tag) { tag.textContent = soldText; tag.hidden = !out; }
      var v = m.variant || p.variants[0];
      var price = '';
      if (pant && v) {
        var q = this.quoteFor(co, offer, cartLines, [pant, v]);
        price = q.qualified ? this.money(q.rows[1].net) + ' ' + copy.inASet : this.money(v.price);
      }
      tile.setAttribute('aria-label', p.shortTitle + ', ' + (p.colour || '') + (price ? ', ' + price : '') + (out ? ', ' + soldText : ''));
    }

    /** A3/A4: the ticked colours in the size chosen above; totals over every unit (main + colours); "Add N to bag". */
    renderPicks(co, offer, cartLines, pant, picks, size) {
      var copy = this.config.copy || {};
      var units = [pant].concat(picks.map(function (p) { return api.sizeVariant(p, size).variant || p.variants[0]; }));
      var quote = this.quoteFor(co, offer, cartLines, units);

      var info = this.querySelector('[data-piece-info]');
      info.textContent = '';
      var name = document.createElement('span'); name.className = 'pdp-v2-set__name'; name.textContent = picks[0].shortTitle; info.appendChild(name);
      var meta = document.createElement('span'); meta.className = 'pdp-v2-set__meta';
      meta.textContent = picks.map(function (p) { return p.colour || p.shortTitle; }).join(', ');
      info.appendChild(meta);

      // The size is carried over, so the panel names it instead of offering chips.
      var sizes = this.querySelector('[data-piece-sizes]');
      sizes.textContent = '';
      // One line of text, not a group of chips.
      ['role', 'aria-labelledby', 'aria-describedby'].forEach(function (a) { sizes.removeAttribute(a); });
      // A7: with no size yet the line is a button to the sizes.
      var line = document.createElement(size === null ? 'button' : 'span'); line.className = 'pdp-v2-set__size-label';
      if (size === null) { line.type = 'button'; line.setAttribute('data-to-size', ''); }
      line.textContent = size === null ? copy.chooseSize || copy.needPantSize : !pant.available ? this.mainOut(size) : fill(copy.sizeLine, { size: shortSize(this.config.pieces || [], size) });
      sizes.appendChild(line);

      var totals = this.querySelector('[data-totals]');
      totals.textContent = '';
      if (quote.qualified) {
        var was = document.createElement('s'); was.textContent = this.money(quote.was);
        var save = document.createElement('span'); save.className = 'pdp-v2-set__save'; save.textContent = this.saveLine(quote, units.length);
        totals.appendChild(was);
        totals.appendChild(document.createTextNode(' ' + this.money(quote.total) + ' '));
        totals.appendChild(save);
      } else {
        totals.textContent = this.money(quote.total);
      }
      var cta = this.querySelector('[data-cta]');
      if (cta && !cta.hasAttribute('aria-busy')) cta.textContent = fill(copy.ctaCount, { count: units.length }) + ' — ' + this.money(quote.total);
      this.renderNote(co, offer, cartLines, pant, quote);
    }

    renderNote(co, offer, cartLines, pant, quote) {
      var copy = this.config.copy || {};
      var note = this.querySelector('[data-note]');
      var alertEl = this.querySelector('[data-alert]');
      if (alertEl && mutation.lastOutcome && mutation.lastOutcome.kind === 'unproven' && !mutation.lastOutcome.dismissed) {
        alertEl.textContent = mutation.lastOutcome.message;
      }
      if (!note || !pant) return;
      var lines = [];
      if (!this.known || this.plain) { note.textContent = ''; note.hidden = true; return; }
      if (quote && !quote.qualified) {
        lines.push(fill(copy.notQualified, { remaining: quote.remaining, collection: this.config.collection, percentage: offer.percentage, min: offer.min }));
      }
      if (quote && quote.bagDrops) lines.push(copy.bagDrops);
      var pq = api.pantQuote(co, offer, cartLines, pant);
      if (pq.bagQualifies && pq.saving > 0) lines.push(fill(copy.bagThisPant, { price: this.money(pq.setPrice) }));
      note.textContent = lines.join(' ');
      note.hidden = lines.length === 0;
    }

    /** M16 (S2): "Make it a set" link above the sticky buttons, v2 only; it carries the slot, so the offer's end hides it. */
    injectStickyLink() {
      if (this.plain || this.expired) return;
      var section = this.closest('.section-main-product');
      var sticky = section ? section.querySelector('sticky-atc') : null;
      if (!sticky) return;
      // A restored snapshot keeps the link it had: adopt it, so expiry and disconnect still remove it.
      var had = sticky.querySelector('.pdp-v2-sticky-set');
      if (had) { this.stickyLink = had; return; }
      var copy = this.config.copy || {};
      var text = Number(this.config.min) === 2 ? copy.stickyBoth : copy.stickyMin;
      if (!text) return;
      var a = document.createElement('a');
      a.className = 'pdp-v2-sticky-set';
      a.href = '#set';
      a.setAttribute('data-offer-slot', String(this.config.slot));
      a.textContent = fill(text, { percentage: this.config.percentage, min: this.config.min });
      // Before the phone size button, if mounted: tab order = visual order.
      var buttons = sticky.querySelector('.pdp-v2-sticky-size') || sticky.querySelector('.sticky-atc__buttons');
      if (buttons && buttons.parentNode) buttons.parentNode.insertBefore(a, buttons); else sticky.appendChild(a);
      this.stickyLink = a;
    }
  }

  if (!customElements.get('pdp-set-row')) customElements.define('pdp-set-row', PdpSetRow);
})();
